import type { ReactNode } from 'react'
import type { IconName } from '../../lib/icons'
import { ICON_PATHS } from '../icons/paths'

/**
 * The parts every figure is drawn from, on one 360 by 120 grid.
 *
 * One grid for all of them, so that every figure is drawn at the same scale wherever it appears and
 * type inside one is the same size as type inside the next. The glyphs are the console's own Phosphor
 * set, drawn from `ICON_PATHS` rather than redrawn, so a device in a figure is the device on the rail.
 *
 * **No color is written here.** Every fill and stroke comes from a `fig-*` class in `styles.css`,
 * each painted with a token that already exists. That is what makes a figure repaint with the palette
 * and with a tenant's brand, and it is what keeps `styles.test.ts` untouched rather than merely
 * passing: text inside a figure is `--text`, `--text-muted`, `--success` or `--danger`, inks already
 * measured on every surface, and it is only ever set on a surface rather than on a tint.
 */
export const WIDTH = 360
export const HEIGHT = 120

/** The square a node's glyph sits in. */
const BOX = 44

export type Tone = 'plain' | 'accent' | 'good' | 'bad'

function toneClass(tone: Tone): string {
  return tone === 'plain' ? '' : ` is-${tone}`
}

/** One Phosphor glyph, centred on a point and scaled from its 256-unit grid. */
export function Glyph({
  name,
  cx,
  cy,
  size = 22,
  tone = 'plain'
}: {
  name: IconName
  cx: number
  cy: number
  size?: number
  tone?: Tone
}) {
  const paths = ICON_PATHS[name]
  const scale = size / 256

  return (
    <g
      className={`fig-glyph${toneClass(tone)}`}
      transform={`translate(${cx - size / 2} ${cy - size / 2}) scale(${scale})`}
    >
      <path d={paths.duotone} opacity="0.2" />
      <path d={paths.solid} />
    </g>
  )
}

/**
 * A thing in a figure: a box with a glyph, its name under it, and a few words under that.
 *
 * The label and the note sit *below* the box on the surface rather than inside it, so they are never
 * set on the accent tint - which is the one fill here that `styles.test.ts` does not measure type on.
 */
export function Node({
  cx,
  cy = 42,
  icon,
  label,
  note,
  tone = 'plain',
  dashed = false,
  badge
}: {
  cx: number
  cy?: number
  icon: IconName
  label: string
  note?: string
  tone?: Tone
  dashed?: boolean
  /** A small glyph on the box's top-right corner: a tick, a cross, a seal. */
  badge?: { icon: IconName; tone: Tone }
}) {
  const half = BOX / 2

  return (
    <g>
      <rect
        className={`fig-box${toneClass(tone)}${dashed ? ' is-dashed' : ''}`}
        x={cx - half}
        y={cy - half}
        width={BOX}
        height={BOX}
        rx={7}
      />
      <Glyph name={icon} cx={cx} cy={cy} tone={tone === 'plain' ? 'plain' : tone} />
      {badge && (
        <>
          <circle className="fig-badge-ground" cx={cx + half - 2} cy={cy - half + 2} r={9} />
          <Glyph name={badge.icon} cx={cx + half - 2} cy={cy - half + 2} size={17} tone={badge.tone} />
        </>
      )}
      <text className="fig-label" x={cx} y={cy + half + 14} textAnchor="middle">
        {label}
      </text>
      {note && (
        <text className="fig-note" x={cx} y={cy + half + 27} textAnchor="middle">
          {note}
        </text>
      )}
    </g>
  )
}

/**
 * A line with a head, and optionally a word riding above its middle.
 *
 * The head is drawn as a polygon rather than an SVG marker. A marker needs an id, an id has to be
 * unique in the document, and a page that draws the same figure twice - the panel behind a card, say -
 * would otherwise hand both arrows whichever marker the browser found first.
 */
export function Arrow({
  x1,
  y1,
  x2,
  y2,
  label,
  dashed = false,
  tone = 'plain'
}: {
  x1: number
  y1: number
  x2: number
  y2: number
  label?: string
  dashed?: boolean
  tone?: 'plain' | 'accent'
}) {
  const angle = Math.atan2(y2 - y1, x2 - x1)
  const length = 7
  const spread = 4
  const backX = x2 - length * Math.cos(angle)
  const backY = y2 - length * Math.sin(angle)
  const offsetX = spread * Math.sin(angle)
  const offsetY = spread * Math.cos(angle)
  const head = `${x2},${y2} ${backX + offsetX},${backY - offsetY} ${backX - offsetX},${backY + offsetY}`

  return (
    <g>
      <line
        className={`fig-line${dashed ? ' is-dashed' : ''}${tone === 'accent' ? ' is-accent' : ''}`}
        x1={x1}
        y1={y1}
        x2={backX}
        y2={backY}
      />
      <polygon className={`fig-head${tone === 'accent' ? ' is-accent' : ''}`} points={head} />
      {label && (
        <text className="fig-note" x={(x1 + x2) / 2} y={Math.min(y1, y2) - 6} textAnchor="middle">
          {label}
        </text>
      )}
    </g>
  )
}

/** Words placed by hand, for the figures whose layout is not a row of nodes. */
export function Words({
  x,
  y,
  children,
  anchor = 'start',
  kind = 'note'
}: {
  x: number
  y: number
  children: ReactNode
  anchor?: 'start' | 'middle' | 'end'
  kind?: 'label' | 'note' | 'good' | 'bad' | 'body'
}) {
  return (
    <text className={`fig-${kind}`} x={x} y={y} textAnchor={anchor}>
      {children}
    </text>
  )
}

/** The horizontal centres of three nodes spread across the grid. */
export const THREE = [60, 180, 300] as const

/** And of four. */
export const FOUR = [45, 135, 225, 315] as const
